import { useState } from 'react';
import { Table, Button, Loader, Center, Modal, Stack, TextInput, Select, Group, Badge, Text, Box } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSchool} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { CreatableSelect, ServerCreatableSelect, QuickYearForm, QuickTeacherForm } from '../components/QuickCreate';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { SectionCard } from '../components/SectionCard';
import { Specialty } from '../components/Specialty';
import { useDebouncedValue } from '@mantine/hooks';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';
import { TEACHER_PARAMS } from '../lib/roles';

interface SchoolClass {
  id: string;
  name: string;
  homeroomTeacher?: { id: string; fullName: string; specialty?: string | null } | null;
  academicYear?: { id: string; name: string };
  _count?: { students: number };
}
interface Year { id: string; name: string }

export default function Classes() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Sinf/o'quv yili yaratish faqat STAFF (SA/DIR/ADM) uchun — HOMEROOM_TEACHER POST /classes'ga kira olmaydi.
  const canManage = canAccess(user?.role, 'staff');
  const [opened, { open, close }] = useDisclosure(false);

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState('name:asc');
  // O'quv yili filtri — backend `academicYearId` ni qo'llab-quvvatlaydi
  const [yearF, setYearF] = useState('');
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    q: qDeb || undefined, academicYearId: yearF || undefined, sort,
  };

  const { items: classes, isLoading, fetching, total, pages, page, setPage, limit, setLimit} = usePagedQuery<SchoolClass>('classes-paged', '/classes', filtrlar, 50);
  const { data: years } = useQuery({
    queryKey: ['academic-years'],
    queryFn: async () => (await api.get<Year[]>('/classes/academic-years')).data,
  });
  const anyFilter = !!(qDeb || yearF);


  const form = useForm({
    initialValues: { name: '', academicYearId: '', homeroomTeacherId: '' },
    validate: { name: (v) => (!v ? t('qc.classNameRequired') : null), academicYearId: (v) => (!v ? t('qc.academicYearRequired') : null) },
  });

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/classes', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('qc.classAdded') });
      // Ro'yxat 'classes-paged' kalitida — 'classes' uni yangilamaydi (prefiks mos emas).
      qc.invalidateQueries({ queryKey: ['classes-paged'] });
      qc.invalidateQueries({ queryKey: ['classes'] });
      form.reset();
      close();
    },
  });

  return (
    <>
      <PageHeader
        title={t('pages.classes.title')}
        subtitle={`${t('common.total')}: ${total}`}
        action={canManage ? <Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.classes.add')}</Button> : undefined}
      />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('classesPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'name:asc', label: t('classesPage.sortNameAsc') },
          { value: 'name:desc', label: t('classesPage.sortNameDesc') },
          { value: 'createdAt:desc', label: t('classesPage.sortNewest') },
          { value: 'students:desc', label: t('classesPage.sortStudentsDesc') },
        ]}
      >
        <Select
          placeholder={t('classesPage.academicYear')}
          data={(years ?? []).map((y) => ({ value: y.id, label: y.name }))}
          value={yearF || null}
          onChange={(v) => { setYearF(v ?? ''); setPage(1); }}
          clearable
          w={160}
        />
        <TableExport<SchoolClass>
          endpoint="/classes"
          params={filtrlar}
          filename={t('nav.classes')}
          ustunlar={[
            { key: 'name', label: t('fields.class') },
            { key: 'year', label: t('qc.academicYear'), get: (r) => r.academicYear?.name ?? '' },
            { key: 'homeroom', label: t('qc.homeroomTeacher'), get: (r) => r.homeroomTeacher?.fullName ?? '' },
            { key: 'students', label: t('nav.students'), get: (r) => r._count?.students ?? 0 },
          ]}
        />
      </TableToolbar>
      <SectionCard title={t('common.list')} icon={IconSchool} p={0}>
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !classes.length ? (
          <Center h={140}><Text c="dimmed">{anyFilter ? t('common.noMatch') : t('pages.classes.empty')}</Text></Center>
        ) : (
          <Box pos="relative">
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('fields.class')}</Table.Th>
                  <Table.Th>{t('qc.academicYear')}</Table.Th>
                  <Table.Th>{t('qc.homeroomTeacher')}</Table.Th>
                  <Table.Th>{t('nav.students')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {classes.map((c) => (
                  <Table.Tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/classes/${c.id}`)}>
                    <Table.Td fw={600}>{c.name}</Table.Td>
                    <Table.Td>{c.academicYear?.name ?? '—'}</Table.Td>
                    <Table.Td>{c.homeroomTeacher ? <>{c.homeroomTeacher.fullName}<Specialty value={c.homeroomTeacher.specialty} /></> : '—'}</Table.Td>
                    <Table.Td><Badge variant="light">{c._count?.students ?? 0}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={close} title={t('pages.classes.add')} centered>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <TextInput label={t('qc.className')} placeholder="5-A" required {...form.getInputProps('name')} />
            <CreatableSelect
              label={t('qc.academicYear')}
              required
              data={(years ?? []).map((y) => ({ value: y.id, label: y.name }))}
              value={form.values.academicYearId}
              onChange={(v) => form.setFieldValue('academicYearId', v || '')}
              error={form.errors.academicYearId}
              createTitle={t('qc.newAcademicYear')}
              renderCreate={(onCreated) => <QuickYearForm onCreated={onCreated} />}
            />
            <ServerCreatableSelect
              label={t('qc.homeroomTeacher')}
              placeholder={t('classesPage.searchTeacher')}
              endpoint="/staff"
              params={TEACHER_PARAMS}
              mapOption={(tt: { id: string; fullName: string }) => ({ value: tt.id, label: tt.fullName })}
              value={form.values.homeroomTeacherId || null}
              onChange={(v) => form.setFieldValue('homeroomTeacherId', v || '')}
              createTitle={t('qc.newTeacher')}
              renderCreate={(onCreated) => <QuickTeacherForm onCreated={onCreated} />}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
